import { useMemo } from 'react';

import { useClientExercises } from '@/features/exercises/hooks';
import { useClientRedFlags } from '@/features/red-flags/hooks';
import { useSessions } from '@/features/sessions/hooks';
import type { AdminSurface } from '@/lib/guards';

import { mergeTimeline } from '../utils/mergeTimeline';
import type { TimelineEvent } from '../types/history';

import { useClientAssessments } from './useClientAssessments';
import { useCopingTypes } from './useCopingTypes';

export const HISTORY_WINDOW_DAYS = 90;

export function useClientHistory(
  clientId: string | undefined,
  surface: AdminSurface = 'workspace',
) {
  const fromISO = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/purity
      new Date(Date.now() - HISTORY_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString(),
    [],
  );

  const sessionsQ = useSessions({
    clientId,
    from: fromISO.slice(0, 10),
    limit: 100,
    sortBy: 'scheduledStart',
    sortOrder: 'desc',
  });
  const redFlagsQ = useClientRedFlags(clientId, { limit: 100 });
  const assessmentsQ = useClientAssessments(clientId);
  const exercisesQ = useClientExercises(clientId, { limit: 100 });
  const copingTypesQ = useCopingTypes(clientId);

  const events: TimelineEvent[] = useMemo(
    () =>
      clientId
        ? mergeTimeline(
            {
              clientId,
              sessions: sessionsQ.data?.data,
              redFlags: redFlagsQ.data?.data,
              assessments: assessmentsQ.data,
              exercises: exercisesQ.data?.data,
              copingTypeChanges: copingTypesQ.data?.changes,
              fromISO,
            },
            surface,
          )
        : [],
    [
      clientId,
      sessionsQ.data,
      redFlagsQ.data,
      assessmentsQ.data,
      exercisesQ.data,
      copingTypesQ.data,
      fromISO,
      surface,
    ],
  );

  return {
    events,
    isLoading:
      sessionsQ.isLoading || redFlagsQ.isLoading || assessmentsQ.isLoading || exercisesQ.isLoading,
    laneErrors: {
      sessions: sessionsQ.error,
      redFlags: redFlagsQ.error,
      assessments: assessmentsQ.error,
      exercises: exercisesQ.error,
    },
    laneRefetch: {
      sessions: sessionsQ.refetch,
      redFlags: redFlagsQ.refetch,
      assessments: assessmentsQ.refetch,
      exercises: exercisesQ.refetch,
    },
    refetchAll: () =>
      Promise.all([
        sessionsQ.refetch(),
        redFlagsQ.refetch(),
        assessmentsQ.refetch(),
        exercisesQ.refetch(),
      ]),
  };
}
